import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";
import { Building2, Camera, Upload, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ComponentType } from "react";

const MAX_BYTES = 5 * 1024 * 1024;

function readPhotoAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

const SectionHeader = ({
  title,
  subtitle,
  icon: Icon,
}: {
  title: string;
  subtitle?: string;
  icon: ComponentType<{ className?: string }>;
}) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
      <Icon className="w-4 h-4 text-primary" />
    </div>
    <div>
      <h3 className="font-display font-semibold text-foreground text-sm">{title}</h3>
      {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
    </div>
  </div>
);

type Props = {
  /** Current file for submit (null if cleared or never set). */
  logoFile: File | null;
  onLogoFileChange: (file: File | null) => void;
  /** Shown in the circle when there is no new file preview (e.g. editing an existing company). */
  existingImageUrl?: string | null;
  /** Called when the user removes the displayed existing image (no new file selected). */
  onRemoveExistingImage?: () => void;
};

export function CompanyLogoUploadSection({
  logoFile,
  onLogoFileChange,
  existingImageUrl,
  onRemoveExistingImage,
}: Props) {
  const [preview, setPreview] = useState<string | null>(null);
  const [captureDialogOpen, setCaptureDialogOpen] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const captureVideoRef = useRef<HTMLVideoElement>(null);
  const captureStreamRef = useRef<MediaStream | null>(null);

  const stopCaptureStream = useCallback(() => {
    captureStreamRef.current?.getTracks().forEach((t) => t.stop());
    captureStreamRef.current = null;
    const v = captureVideoRef.current;
    if (v) v.srcObject = null;
  }, []);

  useEffect(() => {
    if (!logoFile) setPreview(null);
  }, [logoFile]);

  useEffect(() => {
    if (!captureDialogOpen) {
      stopCaptureStream();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Camera is not available in this browser. Use Upload Logo instead.");
      setCaptureDialogOpen(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        captureStreamRef.current = stream;
        const attach = () => {
          const el = captureVideoRef.current;
          if (!el) return false;
          el.srcObject = stream;
          void el.play().catch(() => {});
          return true;
        };
        if (!attach()) {
          window.setTimeout(() => {
            if (!cancelled) attach();
          }, 100);
        }
      } catch {
        if (!cancelled) {
          toast.error("Could not access the camera. Allow permission or use Upload Logo.");
          setCaptureDialogOpen(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      stopCaptureStream();
    };
  }, [captureDialogOpen, stopCaptureStream]);

  const processFile = async (file: File | undefined) => {
    if (!file) return;
    const okType = /^image\/(jpeg|png)$/i.test(file.type) || /\.(jpe?g|png)$/i.test(file.name);
    if (!okType) {
      toast.error("Please choose a JPG or PNG image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Logo must be 5MB or smaller.");
      return;
    }
    try {
      const url = await readPhotoAsDataUrl(file);
      setPreview(url);
      onLogoFileChange(file);
    } catch {
      toast.error("Could not read the logo. Try another file.");
    }
  };

  const clearLogo = () => {
    setPreview(null);
    onLogoFileChange(null);
    if (uploadInputRef.current) uploadInputRef.current.value = "";
  };

  const showExisting = Boolean((existingImageUrl ?? "").trim()) && !preview;
  const displayUrl = preview || (showExisting ? (existingImageUrl ?? "").trim() : null);

  const handleRemoveClick = () => {
    if (preview) {
      clearLogo();
      return;
    }
    if (showExisting && onRemoveExistingImage) {
      onRemoveExistingImage();
    }
  };

  const takePhotoFromCamera = () => {
    const video = captureVideoRef.current;
    if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      toast.error("Camera is not ready yet. Wait a moment and try again.");
      return;
    }
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return;
    const size = Math.min(vw, vh);
    const sx = (vw - size) / 2;
    const sy = (vh - size) / 2;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          toast.error("Could not capture image.");
          return;
        }
        if (blob.size > MAX_BYTES) {
          toast.error("Captured image is too large. Try Upload Logo with a smaller file.");
          return;
        }
        const file = new File([blob], `company-logo-${Date.now()}.jpg`, { type: "image/jpeg" });
        void processFile(file);
        setCaptureDialogOpen(false);
        stopCaptureStream();
      },
      "image/jpeg",
      0.92,
    );
  };

  return (
    <>
      <div className="glass-card p-6">
        <SectionHeader title="Company logo" subtitle="Optional brand mark for cards and listings." icon={Camera} />
        <input
          ref={uploadInputRef}
          type="file"
          accept="image/jpeg,image/png,.jpg,.jpeg,.png"
          className="sr-only"
          onChange={(e) => {
            void processFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <div className="relative shrink-0 mx-auto sm:mx-0">
            <div
              className={cn(
                "flex h-[100px] w-[100px] sm:h-[120px] sm:w-[120px] items-center justify-center rounded-full border-2 border-dashed border-border/60 bg-muted/20 overflow-hidden",
                displayUrl && "border-solid border-border/40",
              )}
            >
              {displayUrl ? (
                <img src={displayUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <Building2 className="h-12 w-12 sm:h-14 sm:w-14 text-muted-foreground/35" strokeWidth={1.25} />
              )}
            </div>
            {preview || (showExisting && onRemoveExistingImage) ? (
              <Button
                type="button"
                size="icon"
                variant="secondary"
                className="absolute -top-1 -right-1 h-7 w-7 rounded-full shadow-md border border-border"
                onClick={handleRemoveClick}
                aria-label="Remove logo"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </div>
          <div className="flex flex-col gap-3 min-w-0 flex-1 items-center sm:items-start">
            <div className="flex flex-wrap justify-center sm:justify-start gap-2">
              <Button type="button" variant="secondary" className="gap-2 h-10" onClick={() => uploadInputRef.current?.click()}>
                <Upload className="h-4 w-4" />
                Upload Logo
              </Button>
              <Button type="button" variant="secondary" className="gap-2 h-10" onClick={() => setCaptureDialogOpen(true)}>
                <Camera className="h-4 w-4" />
                Capture
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center sm:text-left">JPG, PNG up to 5MB. Recommended 400×400px.</p>
          </div>
        </div>
      </div>

      <Dialog
        open={captureDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            stopCaptureStream();
            setCaptureDialogOpen(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Capture logo</DialogTitle>
          </DialogHeader>
          <div className="mx-auto aspect-square w-full max-w-[min(100%,420px)] overflow-hidden rounded-xl bg-black">
            <video ref={captureVideoRef} className="h-full w-full object-cover" autoPlay playsInline muted />
          </div>
          <p className="text-xs text-muted-foreground">Allow camera access, then tap Take photo when you are in frame.</p>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="glass"
              className="w-full sm:w-auto"
              onClick={() => {
                stopCaptureStream();
                setCaptureDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="button" variant="premium" className="w-full gap-2 sm:w-auto" onClick={takePhotoFromCamera}>
              <Camera className="h-4 w-4" />
              Take photo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
